const STORAGE_KEY = '__lookupFavItem';
const STORAGE_VERSION = 4;
const MIN10 = 1000 * 60 * 10;

const isBrowser = typeof window !== 'undefined';

export type ItemType = 'Ad' | 'Hotel';

export type ItemFolder = {
    itemType: ItemType;
    itemId: number;
    folderIds: number[];
};

export type ItemObject = {
    itemId: number;
    itemType: ItemType;
};

export function mkItemFolder(
    itemType: ItemType,
    itemId: number,
    folderIds: number[],
): ItemFolder {
    return { itemType, itemId, folderIds } as ItemFolder;
}

export function mkKey(itemType: ItemType, itemId: number): string {
    return `${itemType}:${itemId}`;
}

// this matches the data shape of the data we get from the servers minimal-v2-endpoint,
/*
{
    "items": [
        { "itemType": "Ad", "itemId": 456789, "folderIds": [ 1 ] },
        { "itemType": "Hotel", "itemId": 666, "folderIds": [ 1 ] },
        { "itemType": "Hotel", "itemId": 456789, "folderIds": [ 1 ] }
    ]
}
 */
class StorageAtom {
    ttl: number;

    version: number;

    userId?: string;

    items: ItemFolder[];

    dataFromServer: boolean;

    constructor(
        ttl: number,
        version: number,
        items: ItemFolder[],
        dataFromServer: boolean,
        userId?: string,
    ) {
        this.ttl = ttl;
        this.version = version;
        this.userId = userId;
        this.items = items;
        this.dataFromServer = dataFromServer;
    }
}

function replaceItems(state: StorageAtom, newItems: ItemFolder[]): StorageAtom {
    return new StorageAtom(
        state.ttl,
        state.version,
        newItems,
        state.dataFromServer,
        state.userId,
    );
}

export class FavoriteStorage {
    userId?: number | string;

    storage: Storage | null;

    ttl: number;

    key: string;

    getNow: () => number;

    storageHandler: (x: StorageEvent) => void;

    constructor(
        onItemUpdate: (key: string, elem: ItemFolder) => void,
        userId?: number | string,
        storage?: Storage,
        ttl?: number,
        key?: string,
        getNow?: () => number,
    ) {
        this.userId = userId;
        this.storage = storage || (isBrowser ? window.localStorage : null);
        this.ttl = ttl || MIN10;
        this.key = key || STORAGE_KEY;
        this.getNow = getNow || ((): number => Date.now());
        this.key = STORAGE_KEY;

        this.storageHandler = (event: StorageEvent): void => {
            try {
                if (event.key !== this.key) {
                    return;
                }
                if (event.oldValue === event.newValue) {
                    return;
                }
                if (!event.newValue) {
                    return;
                }

                const newValue: StorageAtom = JSON.parse(event.newValue || '');
                if (newValue.userId?.toString() !== this.userId?.toString()) {
                    return;
                }
                if (event.oldValue && newValue.items) {
                    const oldValue: StorageAtom = JSON.parse(event.oldValue);

                    newValue.items.forEach((newKey) => {
                        const notPresentInOldValue =
                            oldValue.items != null &&
                            !oldValue.items.find(
                                (i: ItemFolder) =>
                                    i.itemType === newKey.itemType &&
                                    i.itemId === newKey.itemId,
                            );
                        if (notPresentInOldValue) {
                            const newItemFolder =
                                newValue.items.find(
                                    (i: ItemFolder) =>
                                        i.itemType === newKey.itemType &&
                                        i.itemId === newKey.itemId,
                                ) ||
                                mkItemFolder(
                                    newKey.itemType,
                                    newKey.itemId,
                                    [],
                                );
                            onItemUpdate(
                                mkKey(newKey.itemType, newKey.itemId),
                                newItemFolder,
                            );
                        }
                    });
                } else {
                    newValue.items.forEach((newKey) => {
                        const newItemFolder =
                            newValue.items.find(
                                (i: ItemFolder) =>
                                    i.itemType === newKey.itemType &&
                                    i.itemId === newKey.itemId,
                            ) ||
                            mkItemFolder(newKey.itemType, newKey.itemId, []);
                        onItemUpdate(
                            mkKey(newKey.itemType, newKey.itemId),
                            newItemFolder,
                        );
                    });
                }
            } catch (e) {
                /* Ignore all errors */
            }
        };

        if (isBrowser) {
            window.addEventListener('storage', this.storageHandler);
        }
    }

    // mainly used for storybook
    unbind(): void {
        window.removeEventListener('storage', this.storageHandler);
    }

    private createEmptyState(): StorageAtom {
        return new StorageAtom(
            this.getNow() + this.ttl,
            STORAGE_VERSION,
            [],
            false,
            this.userId?.toString(),
        );
    }

    private resetState(): StorageAtom {
        const newState = this.createEmptyState();
        this.storage?.setItem(this.key, JSON.stringify(newState));
        return newState;
    }

    private isValid(state: StorageAtom): StorageAtom {
        if (
            !state ||
            state.version !== STORAGE_VERSION ||
            state.ttl <= this.getNow() ||
            state.userId?.toString() !== this.userId?.toString()
        ) {
            // Clearing potential old state (could be different user)
            return this.resetState();
        }

        return state;
    }

    private getState(): StorageAtom {
        try {
            const state = this.isValid(
                JSON.parse(
                    this.storage?.getItem(this.key) || '',
                ) as StorageAtom,
            );
            if (state) {
                return state;
            }
        } catch (e) {
            // invalid (unparsable) state: reset
            return this.resetState();
        }
        return this.createEmptyState();
    }

    isEmpty(): boolean {
        return !this.getState().dataFromServer;
    }

    // gets folderid[]
    getFolderIds(itemType: ItemType, itemId: number): number[] {
        const pred = (i: ItemFolder): boolean =>
            i.itemType === itemType && i.itemId === itemId;
        const state = this.getState();
        if (state && state.items) {
            if (Array.isArray(state.items)) {
                const found = state.items.find(pred);
                if (found) {
                    return found.folderIds;
                }
            } else {
                return [];
            }
            return [];
        }
        return [];
    }

    // used for manipulating local state
    addToFolder(itemType: ItemType, itemId: number, folderId: number): boolean {
        let entryExists = false;

        function mapper(item: ItemFolder): ItemFolder {
            if (item.itemType === itemType && item.itemId === itemId) {
                entryExists = true; // we'll push the new entry onto a new item
                item.folderIds.push(folderId);
                return item;
            }
            return item;
        }

        try {
            let state = this.getState();
            if (state && state.items && state.items) {
                const newItems = state.items.map(mapper);
                state = replaceItems(state, newItems);
            }
            if (!entryExists) {
                state.items.push(mkItemFolder(itemType, itemId, [folderId]));
            }
            this.storage?.setItem(this.key, JSON.stringify(state));
            return true;
        } catch (e) {
            return false;
        }
    }

    // used for manipulating local state
    removeFromFolder(
        itemType: ItemType,
        itemId: number,
        folderId: number,
    ): boolean {
        function filterFolder(item: ItemFolder): ItemFolder {
            if (item.itemType === itemType && item.itemId === itemId) {
                const newFolders = item.folderIds.filter(
                    (aFolderId: number) => aFolderId !== folderId,
                );
                return mkItemFolder(item.itemType, item.itemId, newFolders);
            }
            return item;
        }

        try {
            let state = this.getState();
            if (state && state.items) {
                const newItems = state.items.map(filterFolder);
                state = replaceItems(state, newItems);
            }
            this.storage?.setItem(this.key, JSON.stringify(state));
            return true;
        } catch (e) {
            return false;
        }
    }

    deleteFolderContent(folderId: number, itemObjects: ItemObject[]): boolean {
        function filterFolder(itemFolder: ItemFolder): ItemFolder {
            const { itemId, itemType } = itemFolder;
            if (
                itemObjects.some(
                    (itemObject) =>
                        itemObject.itemId === itemId &&
                        itemObject.itemType === itemType,
                )
            ) {
                const newFolders = itemFolder.folderIds.filter(
                    (aFolderId: number) => aFolderId !== folderId,
                );
                return mkItemFolder(
                    itemFolder.itemType,
                    itemFolder.itemId,
                    newFolders,
                );
            }
            return itemFolder;
        }

        try {
            let state = this.getState();
            if (state && state.items) {
                const newItems = state.items.map(filterFolder);
                state = replaceItems(state, newItems);
            }
            this.storage?.setItem(this.key, JSON.stringify(state));
            return true;
        } catch (e) {
            return false;
        }
    }

    // used for manipulating local state
    removeFolder(folderId: number): boolean {
        function removeFolder(item: ItemFolder): ItemFolder {
            const newItems = item.folderIds.filter(
                (aFolderId: number) => aFolderId !== folderId,
            );
            return mkItemFolder(item.itemType, item.itemId, newItems);
        }

        try {
            let state = this.getState();
            if (state && state.items) {
                const removed = state.items.map(removeFolder);
                const newItems = removed.filter(
                    (item) => item.folderIds.length > 0,
                );
                state = replaceItems(state, newItems);
            }
            this.storage?.setItem(this.key, JSON.stringify(state));
            return true;
        } catch (e) {
            return false;
        }
    }

    // used to re-set state
    setItems(items: ItemFolder[]): boolean {
        try {
            const state = this.createEmptyState();
            state.items = items;
            state.dataFromServer = true;
            this.storage?.setItem(this.key, JSON.stringify(state));
            return true;
        } catch (e) {
            return false;
        }
    }
}
